import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper, Alert } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getFirestore } from 'firebase/firestore';

const db = getFirestore();

// This component will restrict access to certain routes based on user verification status
const VerificationRequired = ({ children }) => {
  const { currentUser, loading } = useContext(AuthContext);
  const location = useLocation();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!currentUser) {
        setCheckingStatus(false);
        return;
      }

      try {
        // Get user verification status from Firestore
        const userDoc = await db.collection('users').doc(currentUser.uid).get();
        const userData = userDoc.data();
        
        if (userData) {
          setVerificationStatus({
            isVerified: userData.isVerified || false,
            verificationRejected: userData.verificationRejected || false,
            verificationRejectedReason: userData.verificationRejectedReason || '',
            verificationSubmitted: userData.verificationSubmitted || false
          });
        } else {
          setVerificationStatus({
            isVerified: false,
            verificationRejected: false,
            verificationSubmitted: false
          });
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
        setVerificationStatus({
          isVerified: false,
          verificationRejected: false,
          verificationSubmitted: false,
          error: true
        });
      } finally {
        setCheckingStatus(false);
      }
    };

    checkVerificationStatus();
  }, [currentUser]);

  if (loading || checkingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If user is not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is verified, allow access to the protected route
  if (verificationStatus?.isVerified) {
    return children;
  }

  // If verification status check encountered an error
  if (verificationStatus?.error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Une erreur est survenue lors de la vérification de votre compte. Veuillez réessayer plus tard.
          </Alert>
          <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
            Réessayer
          </Button>
        </Paper>
      </Box>
    );
  }

  // If verification has been submitted but not yet approved
  if (verificationStatus?.verificationSubmitted) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Vérification en cours
            </Typography>
          </Box>
          <Typography paragraph>
            Votre demande de vérification d'identité est en cours d'examen. Cette procédure peut prendre jusqu'à 24 heures ouvrables.
          </Typography>
          <Typography paragraph>
            Vous recevrez une notification dès que votre vérification sera traitée. En attendant, certaines fonctionnalités de l'application sont limitées.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Vous pouvez toujours accéder aux fonctionnalités de base de l'application.
          </Alert>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" color="primary" onClick={() => window.history.back()}>
              Retour
            </Button>
            <Button variant="contained" color="primary" component={Link} to="/dashboard">
              Aller au tableau de bord
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // If verification has been rejected
  if (verificationStatus?.verificationRejected) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Vérification refusée
            </Typography>
          </Box>
          <Typography paragraph>
            Votre demande de vérification d'identité a été refusée pour la raison suivante :
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {verificationStatus.verificationRejectedReason || "Document d'identité non valide ou illisible."}
          </Alert>
          <Typography paragraph>
            Vous pouvez soumettre une nouvelle demande avec des documents valides. Assurez-vous que vos documents sont clairement lisibles et non expirés.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" color="primary" onClick={() => window.history.back()}>
              Retour
            </Button>
            <Button variant="contained" color="primary" component={Link} to="/verification/submit">
              Nouvelle demande
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Default case: User needs to verify their identity
  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <VerifiedUserIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Vérification d'identité requise
          </Typography>
        </Box>
        <Typography paragraph>
          Pour accéder à cette fonctionnalité, vous devez vérifier votre identité. Cette procédure est nécessaire pour garantir la sécurité de tous les utilisateurs de notre plateforme.
        </Typography>
        <Typography paragraph>
          La vérification ne prend que quelques minutes. Vous aurez besoin d'une pièce d'identité valide (carte d'identité, passeport ou permis de conduire).
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Vos informations personnelles sont traitées de manière sécurisée et confidentielle, conformément à notre politique de confidentialité.
        </Alert>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" color="primary" onClick={() => window.history.back()}>
            Retour
          </Button>
          <Button variant="contained" color="primary" component={Link} to="/verification/submit">
            Vérifier mon identité
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default VerificationRequired;
